import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import Card from '@/components/Card';
import { Order } from '@/apis/order/interface';
import ImageWithAuthView from '@/components/ImageWithAuthView';
import TextComponent from '@/components/TextComponent';
import PreviewPrescriptionImg from '@/assets/images/order/previewPrescription.png';
import { ReactComponent as NoPrescription } from '@/assets/images/order/noPrescription.svg';
import useModal from '@/hooks/common/useModal';
import ButtonComponent from '@/components/ButtonComponent';
import { ReactComponent as LeftCaretInCircle } from '@/assets/images/order/leftCaretInCircle.svg';
import { ReactComponent as RightCaretInCircle } from '@/assets/images/order/rightCaretInCircle.svg';
import { ReactComponent as FileRemove } from '@/assets/images/common/FileRemove.svg';
import ImageZoomModal from '../containers/ImageZoomModal';
import { KeyActionPairProps, onPressKeyboard } from '@/utils/form';
import _ from 'lodash';
import PreviewZoomWithController, {
  PreviewZoomAreaBox,
  PreviewZoomAreaBoxController,
  PreviewZoomViewBox,
  PreviewZoomViewBoxController
} from '@/pages/OrderDetailPage/containers/PreviewZoomWithController';
import theme from '@/styles/theme';

interface Props {
  order: Order;
}

interface Prescription {
  prescriptionId: number;
  filename: string;
  originalname: string;
  contents?: string;
  reasonTypeDisplayName?: string;
}

const Prescription = (props: Props) => {
  const { order } = props;
  const prescriptions = order.prescriptions ?? [];
  const [PrescriptionModal, setPrescriptionModal] = useModal(false);
  const hasPrescription = !!prescriptions.length;
  const [imageIdx, setImageIdx] = useState(0);
  const [ImageModal, setImageModal] = useModal(false);
  const [offset, setOffset] = useState({ left: 0, top: 0 });
  const uri = hasPrescription
    ? `/images/${prescriptions[imageIdx].prescriptionId}/original`
    : '';
  const fileType = hasPrescription
    ? prescriptions[imageIdx].filename.split('.')[1]
    : '';

  const onClose = () => {
    setImageModal(false);
  };

  const modalKeyBoardAction: Array<KeyActionPairProps> = [
    {
      matchKey: 'Escape',
      action: onClose
    }
  ];

  const renderPrescriptionInfo = () => {
    return (
      <PreviewPrescription marginBottom={fileType !== 'pdf'}>
        {prescriptions.map((item: Prescription, idx: number) => (
          <PreviewPrescriptionItem
            src={PreviewPrescriptionImg}
            key={item.prescriptionId}
            onClick={() => setImageIdx(idx)}
            active={imageIdx === idx}
          />
        ))}
      </PreviewPrescription>
    );
  };

  const prevImage = () => {
    setImageIdx(imageIdx - 1);
  };
  const nextImage = () => {
    setImageIdx(imageIdx + 1);
  };

  const _setOffset = _.throttle(e => {
    setOffset({
      left: e.pageX,
      top: e.pageY
    });
  }, 500);

  useEffect(() => {
    // 가격 설정 하기 전
    // 결제완료(유저) => 픽업요청 하기전 (조제단계)
    if (
      order.payStatus === 'none' ||
      (order.payStatus === 'done' && !order.callAt)
    ) {
      if (order.prescriptions.length > 1) {
        setPrescriptionModal(true);
      }
    }
  }, [order]);

  return (
    <Wrapper
      onKeyUp={e => onPressKeyboard(modalKeyBoardAction, e.key)}
      onMouseMove={e => {
        _setOffset(e);
      }}>
      <PreviewZoomWithController>
        <Card>
          <CardBody>
            {!!prescriptions[0]?.reasonTypeDisplayName && (
              <PrescriptionModifiedNotice>
                <div>
                  <FileRemove
                    style={{ width: 16, height: 16, marginRight: 10 }}
                  />
                  처방전이 수정되었어요.
                </div>
                {!!prescriptions[0]?.contents ? (
                  <div>
                    수정 사유 : {prescriptions[0].reasonTypeDisplayName} / 수정
                    내용 : {prescriptions[0].contents}
                  </div>
                ) : (
                  <div>
                    수정 사유 : {prescriptions[0].reasonTypeDisplayName}
                  </div>
                )}
              </PrescriptionModifiedNotice>
            )}
            <PrevButton
              show={+(hasPrescription && !!imageIdx)}
              onClick={prevImage}
            />
            <ImageWrapper>
              {hasPrescription ? (
                <>
                  <ImageTop>
                    <PreviewZoomAreaBoxController />
                    <PreviewZoomViewBoxController />
                  </ImageTop>
                  <ImageBody>
                    {renderPrescriptionInfo()}
                    <ImageView>
                      <PreviewZoomAreaBox
                        offset={offset}
                        onClick={() => {
                          setImageModal(true);
                        }}
                      />
                      <ImageWithAuthView
                        orderId={order.id}
                        id={'preview'}
                        scale={1}
                        uri={uri}
                        fileType={fileType}
                        imageZoom={setImageModal}
                        originalName={prescriptions[imageIdx]?.originalname}
                        doctorInfo={order.doctorInfo}
                      />
                    </ImageView>
                  </ImageBody>
                </>
              ) : (
                <NoPrescriptionWrapper>
                  <NoPrescription />
                  <TextComponent>
                    발행된 처방전이 없거나 {'\n'}기한이 지난 처방전 입니다.
                  </TextComponent>
                </NoPrescriptionWrapper>
              )}
            </ImageWrapper>
            <NextButton
              show={+(hasPrescription && imageIdx + 1 !== prescriptions.length)}
              onClick={nextImage}
            />
          </CardBody>
        </Card>
        <PreviewZoomViewBox offset={offset} uri={uri} filetype={fileType} />
        <PrescriptionModal clickOverlayToClose={false}>
          <ModalTop>
            <ModalTitle>
              <TitleText>처방전이</TitleText>
              <TitleText color={theme.DF_COLOR.PRIMARY500}>
                &nbsp;{prescriptions.length}개
              </TitleText>
              <TitleText>&nbsp;입니다</TitleText>
            </ModalTitle>
            <ModalBody>
              <TextComponent>꼭 확인하시고 조제 부탁드립니다.</TextComponent>
            </ModalBody>
          </ModalTop>
          <ModalBottom>
            <ButtonComponent
              borderColor={theme.DF_COLOR.WHITE}
              bgColor={theme.DF_COLOR.WHITE}
              textColor={theme.DF_COLOR.BLACK}
              text={'확인'}
              padding={'1rem'}
              onClick={() => setPrescriptionModal(false)}
              style={{ width: '100%' }}
            />
          </ModalBottom>
        </PrescriptionModal>
        <ImageModal clickOverlayToClose={false}>
          <ImageZoomModal
            order={order}
            setImageModal={setImageModal}
            imageIdx={imageIdx}
            setImageIdx={setImageIdx}
            originalName={prescriptions[imageIdx]?.originalname}
          />
        </ImageModal>
      </PreviewZoomWithController>
    </Wrapper>
  );
};

export default Prescription;

const Wrapper = styled.div`
  width: 100%;
`;

const CardBody = styled.div`
  display: flex;
  flex-flow: column;
  position: relative;
  justify-content: center;
  align-items: center;
  padding: 12px 16px;
  min-height: 51rem;
  box-sizing: border-box;
`;

const ImageWrapper = styled.div``;

const ImageTop = styled.div`
  padding: 1.5rem 0;
  display: flex;
  margin-right: -1.25rem;
`;

// height: 45rem;
const ImageBody = styled.div`
  display: flex;
  gap: 12px;
`;

const ImageView = styled.div`
  position: relative;
`;

const NoPrescriptionWrapper = styled.div`
  text-align: center;

  > * {
    &:first-child {
      margin-bottom: 25px;
    }
  }
`;

const PrevButton = styled(LeftCaretInCircle)<{ show: number }>`
  ${({ show }) => !show && 'display: none'};
  position: absolute;
  left: 15px;
  cursor: pointer;
  z-index: 10;
`;

const NextButton = styled(RightCaretInCircle)<{ show: number }>`
  ${({ show }) => !show && 'display: none'};
  position: absolute;
  right: 15px;
  cursor: pointer;
  z-index: 10;
`;

const PreviewPrescription = styled.div<{ marginBottom: boolean }>`
  ${({ marginBottom }) => marginBottom && 'margin-bottom: 30px'};
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  gap: 1rem 0;
`;

const PreviewPrescriptionItem = styled.img<{ active: boolean }>`
  width: 50px;
  height: 72px;
  ${({ active }) => active && `border: 2px solid ${theme.DF_COLOR.PRIMARY500}`};
  cursor: pointer;
`;

const ModalTop = styled.div`
  padding: 2.7rem;
`;
const ModalTitle = styled.div`
  display: flex;
  justify-content: center;
  flex-flow: row;
  margin-bottom: 18px;
`;

const TitleText = styled(TextComponent)<{ color?: string }>`
  color: ${({ color }) => color && `color: ${color}`};
  font-size: 22px;
  font-weight: 700;
`;
const ModalBody = styled.div`
  text-align: center;
`;

const ModalBottom = styled.div`
  text-align: center;
  border-top: 1px solid #d1d1d1;
`;

const PrescriptionModifiedNotice = styled.div`
  width: 100%;
  padding: 14px 16px;
  background: ${({ theme }) => theme.DF_COLOR.RED100};
  height: 48px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: ${({ theme }) => theme.DF_COLOR.RED700};
  ${({ theme }) => theme.DF_TYPOGRAPHY.UI14SB};
  border-radius: 8px;
  margin: 12px 16px;

  & > div {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
`;
